import { API, CreateAPIRequest, UpdateAPIRequest, APIListResponse } from '../types/api';
import { apiConfig } from '../config/api';
import { retryApiCall } from '../utils/apiUtils';

class APIService {
  private getBaseUrl(): string {
    return process.env.REACT_APP_API_BASE_URL || apiConfig.baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.getBaseUrl()}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add timeout support
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout);
    
    config.signal = controller.signal;

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || error.message || 'API request failed');
      }

      // Handle empty responses (common for DELETE operations)
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      if (contentLength === '0' || !contentType?.includes('application/json')) {
        return undefined as T;
      }

      // Check if response has content before parsing JSON
      const text = await response.text();
      if (!text.trim()) {
        return undefined as T;
      }

      return JSON.parse(text);
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  private async requestWithRetry<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    return retryApiCall(
      () => this.request<T>(endpoint, options),
      apiConfig.retryAttempts,
      apiConfig.retryDelay
    );
  }

  async getAPIs(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    tags?: string;
  }): Promise<APIListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.tags) searchParams.append('tags', params.tags);

    const queryString = searchParams.toString();
    const endpoint = `/v1/apis${queryString ? `?${queryString}` : ''}`;
    
    return this.requestWithRetry<APIListResponse>(endpoint);
  }

  async getAPI(id: string): Promise<API> {
    return this.requestWithRetry<API>(`/v1/apis/${id}`);
  }

  async createAPI(data: CreateAPIRequest): Promise<API> {
    return this.request<API>('/v1/apis', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAPI(id: string, data: UpdateAPIRequest): Promise<API> {
    return this.request<API>(`/v1/apis/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAPI(id: string, soft: boolean = false): Promise<void> {
    const endpoint = `/v1/apis/${id}${soft ? '?soft=true' : ''}`;
    await this.request<void>(endpoint, {
      method: 'DELETE',
    });
  }

  /**
   * Analyze README file and extract API metadata
   */
  async analyzeReadme(file: File): Promise<{
    extracted_data: {
      name: string;
      description: string;
      url: string;
      tags: string[];
      payloadDescription: string;
    };
    confidence_scores: {
      name: number;
      description: number;
      url: number;
      tags: number;
      payloadDescription: number;
    };
    analysis_time_ms: number;
    metadata: {
      originalName: string;
      size: number;
      uploadedAt: string;
    };
  }> {
    const formData = new FormData();
    formData.append('readme', file);

    const url = `${this.getBaseUrl()}/v1/apis/analyze-readme`;
    
    // For FormData, don't set Content-Type header - let browser set it with boundary
    const config: RequestInit = {
      method: 'POST',
      body: formData,
    };

    // Add timeout support
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout);
    
    config.signal = controller.signal;

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || error.message || 'README analysis failed');
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Generate test data for an API using AI
   */
  async generateTestData(id: string): Promise<{
    apiId: string;
    testData: any;
    generatedAt: string;
    metadata: {
      name: string;
      description: string;
      payloadDescription?: string;
    };
  }> {
    return this.request<{
      apiId: string;
      testData: any;
      generatedAt: string;
      metadata: {
        name: string;
        description: string;
        payloadDescription?: string;
      };
    }>(`/v1/generate-test-data/${id}`, {
      method: 'POST',
    });
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request<{ status: string; timestamp: string }>('/health');
  }

  /**
   * Get API statistics
   */
  async getAPIStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    deprecated: number;
  }> {
    return this.requestWithRetry<{
      total: number;
      active: number;
      inactive: number;
      deprecated: number;
    }>('/v1/apis/stats');
  }
}

export const apiService = new APIService();

// Named exports for individual methods (useful for testing)
export const createAPI = (data: CreateAPIRequest) => apiService.createAPI(data);
export const analyzeReadme = (file: File) => apiService.analyzeReadme(file);
export const generateTestData = (id: string) => apiService.generateTestData(id);

export default apiService;
